import type { CSSProperties, ReactNode } from 'react';
import { CircleAlert, CircleCheck, Info, TriangleAlert, OctagonAlert, ShieldCheck } from 'lucide-react';
import { useT } from '../i18n';
import { isApiError } from '../api/client';
import { LogoLoader, LogoMark } from './Logo';
import { useDelayedFlag } from '../lib/hooks';
import { Button } from './Button';

type BannerTone = 'info' | 'success' | 'warning' | 'danger' | 'review' | 'neutral';
const BANNER_ICON: Record<BannerTone, typeof Info> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: OctagonAlert,
  review: ShieldCheck,
  neutral: Info,
};

/** Banner: info / success / warning / danger. Blocking banners (overdue, out of scope) get no close button (§8.4). */
export function Banner({
  tone = 'info',
  title,
  children,
  actions,
  icon,
  live,
}: {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
  live?: boolean;
}) {
  const Icon = BANNER_ICON[tone];
  return (
    <div className={`banner banner--${tone}`} role={tone === 'danger' ? 'alert' : live ? 'status' : undefined}>
      <span className="banner__icon">{icon ?? <Icon size={20} strokeWidth={1.75} aria-hidden="true" />}</span>
      <div className="banner__body">
        {title && <div className="banner__title">{title}</div>}
        {children && <div className="banner__text">{children}</div>}
      </div>
      {actions && <div className="banner__actions">{actions}</div>}
    </div>
  );
}

export function Skeleton({ w = '100%', h = 16, r, style }: { w?: number | string; h?: number | string; r?: number; style?: CSSProperties }) {
  return <span className="skeleton" style={{ width: w, height: h, borderRadius: r, ...style }} aria-hidden="true" />;
}

/** Skeleton block shown only after 300 ms of loading (§9.1). */
export function SkeletonBlock({ lines = 4, card = true, loading = true }: { lines?: number; card?: boolean; loading?: boolean }) {
  const t = useT();
  const show = useDelayedFlag(loading);
  const inner = (
    <div className="stack-sm" aria-busy="true" aria-label={t('common.loading')}>
      {show &&
        Array.from({ length: lines }, (_, i) => <Skeleton key={i} h={i === 0 ? 22 : 16} w={i === 0 ? '40%' : `${90 - ((i * 13) % 35)}%`} />)}
    </div>
  );
  return card ? <div className="card">{inner}</div> : inner;
}

export function PageLoader() {
  const t = useT();
  const show = useDelayedFlag(true, 200);
  return <div className="page-loader">{show && <LogoLoader size={48} label={t('common.loading')} />}</div>;
}

/** Empty state: small clock-face graphic + title + one sentence + one action (§8.4). */
export function EmptyState({ title, text, action, icon }: { title: ReactNode; text?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty">
      {icon ?? <LogoMark size={40} />}
      <div className="empty__title">{title}</div>
      {text && <p className="empty__text">{text}</p>}
      {action}
    </div>
  );
}

/**
 * Plain-language error: what happened, what you can do, whether data was saved — code in small print (§9.2).
 */
export function ErrorState({
  error,
  onRetry,
  compact,
  savedNote = true,
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
  /** "Wat je al had ingevuld, is bewaard" — off where we cannot promise it (a crashed page, DC-01). */
  savedNote?: boolean;
}) {
  const t = useT();
  const { what, todo, code } = describeError(error, t);
  // A 401 here means the session ended (expired, logged out elsewhere, or the test data was reset): offer a fresh login
  // instead of a retry that can only fail again.
  const sessionEnded = isApiError(error) && (error.code === 'unauthenticated' || error.status === 401);
  const relogin = () => {
    const next = window.location.pathname + window.location.search;
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  };
  const body = (
    <div className="error-state">
      <strong>{what}</strong>
      <span>{todo}</span>
      {savedNote && !sessionEnded && <span className="muted small">{t('error.savedNote')}</span>}
      {sessionEnded ? (
        <div>
          <Button size="sm" variant="primary" onClick={relogin}>
            {t('error.session.login')}
          </Button>
        </div>
      ) : onRetry && (
        <div>
          <Button size="sm" variant="secondary" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        </div>
      )}
      <span className="error-state__code">{code}</span>
    </div>
  );
  if (compact) return body;
  return <Banner tone="danger" icon={<CircleAlert size={20} aria-hidden="true" />}>{body}</Banner>;
}

export function describeError(error: unknown, t: ReturnType<typeof useT>): { what: string; todo: string; code: string } {
  const code = isApiError(error) ? error.code : 'unknown';
  const status = isApiError(error) ? error.status : 0;
  if (code === 'unauthenticated' || status === 401)
    return { what: t('error.session.what'), todo: t('error.session.todo'), code: `ERR_UNAUTHENTICATED · ${status || 401}` };
  switch (code) {
    case 'network':
      return { what: t('error.network.what'), todo: t('error.network.todo'), code: 'ERR_NETWORK' };
    case 'forbidden':
      return { what: t('error.forbidden.what'), todo: t('error.forbidden.todo'), code: `ERR_FORBIDDEN · ${status}` };
    case 'not_found':
      return { what: t('error.notFound.what'), todo: t('error.notFound.todo'), code: `ERR_NOT_FOUND · ${status}` };
    case 'validation':
      return { what: t('error.validation.what'), todo: t('error.validation.todo'), code: `ERR_VALIDATION · ${status}` };
    case 'gate_blocked': {
      // Import gate (Sprint 2A, TS-03): the admission questions are incomplete or the company does not fit self-service.
      const reason = isApiError(error) ? (error.details as { reason?: string } | undefined)?.reason : undefined;
      if (reason === 'intake_incomplete') return { what: t('error.gate.what'), todo: t('error.intake.incomplete'), code: `ERR_INTAKE_INCOMPLETE · ${status}` };
      if (reason === 'not_eligible') return { what: t('error.gate.what'), todo: t('error.intake.notEligible'), code: `ERR_NOT_ELIGIBLE · ${status}` };
      return { what: t('error.gate.what'), todo: t('error.gate.todo'), code: `ERR_GATE_BLOCKED · ${status}` };
    }
    case 'read_only':
      return { what: t('error.readOnly.what'), todo: t('error.readOnly.todo'), code: `ERR_READ_ONLY · ${status}` };
    case 'payment_required':
      return { what: t('error.payment.what'), todo: t('error.payment.todo'), code: `ERR_PAYMENT_REQUIRED · ${status}` };
    case 'conflict':
      return { what: t('error.conflict.what'), todo: t('error.conflict.todo'), code: `ERR_CONFLICT · ${status}` };
    case 'feature_disabled':
      return { what: t('error.featureDisabled.what'), todo: t('error.featureDisabled.todo'), code: `ERR_FEATURE_DISABLED · ${status}` };
    case 'reauth_required':
      return { what: t('error.reauth.what'), todo: t('error.reauth.todo'), code: `ERR_REAUTH · ${status}` };
    default:
      return { what: t('error.generic.what'), todo: t('error.generic.todo'), code: `ERR_${String(code).toUpperCase()}${status ? ` · ${status}` : ''}` };
  }
}

/** Short message for toasts and inline field errors. */
export function errorMessage(error: unknown, t: ReturnType<typeof useT>): string {
  const d = describeError(error, t);
  return `${d.what} ${d.todo}`;
}
